import * as api from "../scripts/api";
import { bindData } from "../scripts/binding";
import { partition } from "../scripts/util";
import { showBeacon } from "../scripts/beacons";
import { AchievementsResponse } from "../../functions/lib/json-api";

// DOM elements
const modal = document.getElementById("achievements-modal") as HTMLDivElement;
const container = modal.querySelector<HTMLParagraphElement>(
  '[data-container="achievement-list"]',
)!;
const error = modal.querySelector<HTMLDivElement>('[data-container="error"]')!;
const template = document.getElementById(
  "achievement-card",
) as HTMLTemplateElement;
const notificationTemplate = document.getElementById(
  "achievement-notification",
) as HTMLTemplateElement;
const noticeTemplate = document.getElementById(
  "achievement-card",
) as HTMLTemplateElement;

const earnedAchievements: Set<string> = new Set();

export async function loadAchievements() {
  const achievements = await api.getAchievements();

  if (!achievements) {
    error.textContent = "Unable to load achievements";
    error.hidden = false;
    return;
  }

  bindData({
    "achievements-total": achievements.length.toLocaleString(),
    "achievements-earned": achievements.filter((a) => a.achieved).length
      .toLocaleString(),
  });

  achievements.forEach((a) => {
    const card = cloneTemplate<HTMLDivElement>(template);
    container.appendChild(card);
    card.id = `achievement-card-${a.id}`;
    bindData({
      "achievement-icon": a.achieved
        ? "🏆"
        : '<i class="bi-circle has-text-grey">',
      "achievement-title": a.title,
      "achievement-description": a.description,
    }, card);

    if (a.achieved) earnedAchievements.add(a.id);
  });
}

export function processAchievements(
  positionAchievements: AchievementsResponse,
) {
  // Check if there are new achievements that aren't in earnedAchievements
  const newAchievements = positionAchievements.filter((a) =>
    !earnedAchievements.has(a.id)
  );
  if (!newAchievements.length) return;

  const [achieved, missed] = partition(newAchievements, (a) => a.achieved);

  // Get the achievement-notification template and append it to the body
  const notification = cloneTemplate<HTMLDivElement>(notificationTemplate);
  document.body.appendChild(notification);

  // Get the achievement-notice template and append it to the notification for each achievement
  [...achieved, ...missed].forEach((a) => {
    const notice = cloneTemplate<HTMLDivElement>(noticeTemplate);
    notification.appendChild(notice);

    // Set the achievement name and description
    bindData({
      "achievement-icon": a.achieved ? "🏆" : "⭕️",
      "achievement-type": a.achieved ? "Achieved!" : "Missed!",
      "achievement-title": a.title,
      "achievement-description": a.description,
    }, notice);

    // Update the achievement card to show the correct icon
    const card = document.getElementById(
      `achievement-card-${a.id}`,
    ) as HTMLDivElement;
    if (card) {
      bindData({
        "achievement-icon": a.achieved
          ? "🏆"
          : '<i class="bi-circle has-text-grey">',
      }, card);
    }
  });

  // Set the opacity to 1 so it fades in
  notification.style.opacity = "1";

  notification.addEventListener("click", removeNotification);

  // Show the beacon if this is the first achievement
  if (earnedAchievements.size === 0 && achieved.length > 0) {
    showBeacon("achievements");
  }

  // Add the new achievements to the earnedAchievements set
  achieved.forEach((a) => earnedAchievements.add(a.id));
  positionAchievements = [];

  bindData({ "achievements-earned": earnedAchievements.size.toLocaleString() });
}

// Fade out and remove the notification
export function removeNotification() {
  const notification = document.querySelector<HTMLDivElement>(
    "#notification-popover",
  );
  if (!notification) return;

  notification.style.opacity = "0";
  setTimeout(() => {
    notification.remove();
  }, 500);
}

// cloneTemplate returns a generic that descends from Node
function cloneTemplate<T extends Node>(template: HTMLTemplateElement): T {
  return template.content.firstElementChild!.cloneNode(true) as T;
}
